/**
 * Financials dashboard — data loader + pure math (Aug 21, 2026).
 *
 * Single source of truth for every number on /financials. The page is
 * presentational; everything computable lives here so the SQL replay
 * verification can diff against one module.
 *
 * Reuses the shared money math — commission cost per deal comes from
 * CommissionBoard's groupByPerson (identical inputs via COMMISSION_DEAL_SELECT)
 * plus the 1.5% RS pool, so this board reconciles with Finance → Commissions
 * to the penny. Revenue basis: recognized at build completion. Cash items
 * bucket by their own payment/paid dates.
 *
 * ⚠ PostgREST returns numerics as STRINGS — every read goes through num().
 */

import { supabase } from "./supabase";
import { mergeRates, ratesMapFromRows } from "./commissionRates";
import { calcScope } from "./scopeCalc";
import {
  groupByPersonStored,
  COMMISSION_DEAL_SELECT,
  type CommissionDeal,
  type CanvasserRef,
} from "../components/commissions/CommissionBoard";
import {
  fetchCommissionPayments,
  indexPayments,
  storedRsPoolLines,
  type CommissionPaymentRow,
} from "./commissionPayments";
import type { Deal, TeamMember, MaterialInvoice } from "./types";

export function num(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

// ── Deal shape ────────────────────────────────────────────────────────────

export type FinDeal = CommissionDeal &
  Pick<
    Deal,
    | "final_invoice_amount"
    | "initial_invoice_amount"
    | "pre_build_amount_received"
    | "post_build_amount_received"
    | "pre_build_amount_in_flight"
    | "post_build_amount_in_flight"
    | "build_scheduled_date"
    | "insurance_company"
    | "property_zip"
    | "property_city"
    | "install_agreement_signed"
    | "balance_written_off"
    | "non_commissionable"
    // Oct 29, 2026 — the trade money calcScope reads (migration 20261029f)
    | "trade_ho_total"
    | "trade_ho_floor"
    | "trade_ho_lines"
  > & { lead_source?: string | null };

// Oct 29, 2026: + trade_ho_total, trade_ho_floor, trade_ho_lines (the trade billed to the homeowner is in the
// invoice, the backlog and the cash at completion). trade_margin_loss and is_retail come with
// COMMISSION_DEAL_SELECT.
export const FIN_DEAL_SELECT =
  COMMISSION_DEAL_SELECT +
  ",final_invoice_amount,initial_invoice_amount,pre_build_amount_received,post_build_amount_received," +
  "pre_build_amount_in_flight,post_build_amount_in_flight,build_scheduled_date,insurance_company," +
  "balance_written_off,non_commissionable," +
  "property_zip,property_city,install_agreement_signed,lead_source," +
  "trade_ho_total,trade_ho_floor,trade_ho_lines";

/**
 * QuickBooks-booked revenue by month — the basis Greg has always used for
 * fiscal-year reporting (revenue recognized when INVOICED). Kept alongside the
 * board's build-completion basis so both can be read side by side while the
 * company transitions. Hand-imported from P&L by Month exports; there is no
 * live QBO feed. Roofing only — SSP Revenue is excluded at import.
 */
export interface ExternalRevenueRow {
  ym: string;
  company: string;
  /** 'booked' = QuickBooks invoice-date revenue. 'production_legacy' = the Zoho
   *  export (initial invoice, dated by preferred build date) covering the years
   *  before this CRM was the system of record. */
  basis: string | null;
  revenue: number | string | null;
  /** Supplement portion of the booked revenue. NULL = not imported for that
   *  month — unknown, NOT zero. Readers must surface the gap. */
  supplement_revenue: number | string | null;
  supplement_itemized: boolean | null;
  bad_debt: number | string | null;
  late_fee: number | string | null;
  deal_count: number | null;
  is_partial: boolean | null;
  note: string | null;
}

/** One row per month of the official revenue series (view: revenue_series_monthly). */
export interface RevenueSeriesRow {
  ym: string;
  revenue: number | string | null;
  revenue_source: string | null;
  supplements: number | string | null;
  supplement_source: string | null;
  bad_debt: number | string | null;
  late_fee: number | string | null;
  builds: number | null;
}

export interface FinPayment {
  id: string;
  deal_id: string | null;
  slot: string | null;
  amount: number | string | null;
  state: string | null;
  method: string | null;
  bank_cleared_at: string | null;
  iink_cleared_at: string | null;
  stripe_cleared_at: string | null;
  updated_at: string | null;
}

export interface FinCrewPayment {
  id: string;
  deal_id: string | null;
  squares: number | string | null;
  total: number | string | null;
  status: string | null;
  paid_at: string | null;
  approved_at: string | null;
}

export interface ClaimRow {
  deal_id: string;
  carrier: string;
  zip: string;
  city: string;
  filed: string; // yyyy-mm-dd
  approved: boolean;
  denied: boolean;
  lost: boolean;
  built: boolean;
}

/**
 * One row per deal with an adjuster meeting (1st or 2nd) OR a completed
 * pre-inspection, from financials_rs_rows. Deliberately NOT derived from the
 * claims rows: 72 of ~309 deals with a 2026 meeting have no
 * claim_called_in_date stamped, so the claims RPC would under-count meetings
 * ~23%. Outcome flags use the same classifier as the claims lens (copied
 * server-side — change both together).
 */
export interface RsRow {
  deal_id: string;
  rs: string | null; // assigned_rs_id — null = nobody assigned
  met: string | null; // adjuster_meeting_date, ET calendar day yyyy-mm-dd
  met2: string | null; // reinspection (2nd adjuster) meeting day
  approved: boolean;
  denied: boolean;
  lost: boolean;
  built: boolean;
  /** The climber: pre_inspection_assigned_to, falling back to the deal's RS
   *  when the climb wasn't explicitly assigned (server-side coalesce — the
   *  climb workflow batches climbs to the deal's own RS). */
  pre_by: string | null;
  pre_done: boolean;
  /** Climb period anchor: pre_inspection_completed_date, else the 1st
   *  adjuster-meeting day the climb served. */
  pre_on: string | null;
  /** Went through the reinspection path (partial approval or a contested
   *  denial) at any point before its current outcome. */
  reinsp: boolean;
  /** lower(trim(insurance_company)) — "" when not recorded. Same normalization
   *  as ClaimRow.carrier so one filter key works across both. */
  carrier: string;
}

export interface CycleStat { metric: string; median_days: number | null; n: number }

export interface FinTargets { revenue: number; builds: number; margin_pct: number }

/**
 * What is still OWED on a material invoice. Partial payments recorded by the
 * SRS payment-history import (paid_amount) net out; a paid invoice owes 0.
 * Unpaid credit memos stay negative so they keep reducing the SRS balance
 * exactly as before.
 */
export function invoiceOwed(i: Pick<MaterialInvoice, "paid_status" | "total" | "paid_amount">): number {
  if (i.paid_status !== "unpaid") return 0;
  return num(i.total) - num(i.paid_amount ?? 0);
}

export interface FinancialsRaw {
  deals: FinDeal[];
  payments: FinPayment[];
  crew: FinCrewPayment[];
  invoices: MaterialInvoice[];
  /** deal_id is set only on the 5-Star Review SPIFF (reviewSpiffByDeal) */
  spiffs: Array<{ amount_cents: number | string | null; paid_at: string | null; deal_id?: string | null }>;
  canvasserPayouts: Array<{ total: number | string | null; status: string | null; paid_at: string | null }>;
  members: Record<string, TeamMember>;
  /** Sep 25, 2026 — the stored payout ledger (finance tier reads every row). */
  commissionPayments: CommissionPaymentRow[];
  claims: ClaimRow[];
  rsRows: RsRow[];
  monthlyOverhead: number;
  targets: FinTargets;
  /** Empty when the QBO import is absent or unreadable — never blanks the board. */
  externalRevenue: ExternalRevenueRow[];
  /** The official revenue series. Empty if unreadable — never blanks the board. */
  revenueSeries: RevenueSeriesRow[];
}

// Cleared / in-flight state sets — mirror recompute_deal_payment_summary.
const CLEARED_STATES = new Set(["bank_cleared", "cleared"]);
const IN_FLIGHT_STATES = new Set([
  "collected", "iink_submitted", "mortgage_endorsed", "fedex_out",
  "fedex_back", "iink_cleared", "deposited", "made",
]);
const TERMINAL_STAGES = new Set([
  "closed_lost", "cancelled", "no_thanks", "insufficient_damage",
  "closed_inspection", "claims_denied",
]);

export function clearedDate(p: FinPayment): string | null {
  return p.bank_cleared_at ?? p.iink_cleared_at ?? p.stripe_cleared_at ?? p.updated_at ?? null;
}

// ── Loader ────────────────────────────────────────────────────────────────

/**
 * PostgREST caps responses at ~1,000 rows — a plain select silently truncates
 * (the documented Canvas pin-cap bug class). material_invoices is already at
 * 1,428 rows and deals will cross the cap too, so the big tables page through
 * .range() until a short page.
 */
async function fetchAll<T>(
  ranged: (from: number, to: number) => PromiseLike<{ data: unknown; error: { message: string } | null }>,
): Promise<T[]> {
  const PAGE = 1000;
  const out: T[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await ranged(from, from + PAGE - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as T[];
    out.push(...rows);
    if (rows.length < PAGE) break;
    if (from > 50_000) break; // runaway guard
  }
  return out;
}

const FIN_DEAL_OR =
  "build_completed_date.not.is.null,install_agreement_signed.eq.true," +
  "stage.in.(collecting,delinquent),commission_ready_date.not.is.null";

export async function fetchFinancialsRaw(): Promise<FinancialsRaw> {
  const dealsP = fetchAll<FinDeal>(
    (a, b) => supabase.from("deals").select(FIN_DEAL_SELECT).or(FIN_DEAL_OR).order("id").range(a, b),
  );
  const extRevP = supabase
    .from("external_monthly_revenue")
    .select("ym,company,basis,revenue,supplement_revenue,supplement_itemized,bad_debt,late_fee,deal_count,is_partial,note")
    .order("ym");
  const seriesP = supabase.from("revenue_series_monthly")
    .select("ym,revenue,revenue_source,supplements,supplement_source,bad_debt,late_fee,builds")
    .order("ym");
  const payP = fetchAll<FinPayment>(
    (a, b) => supabase.from("deal_payments")
      .select("id,deal_id,slot,amount,state,method,bank_cleared_at,iink_cleared_at,stripe_cleared_at,updated_at")
      .order("id").range(a, b),
  );
  const invP = fetchAll<MaterialInvoice>(
    (a, b) => supabase.from("material_invoices").select("*").order("id").range(a, b),
  );
  const [deals, payments, invoices, crewQ, spiffQ, canvQ, rosterQ, claimsQ, rsQ, settingsQ, extRevQ, seriesQ, ratesQ, commissionPayments] =
    await Promise.all([
      dealsP,
      payP,
      invP,
      supabase
        .from("roof_crew_payments")
        .select("id,deal_id,squares,total,status,paid_at,approved_at"),
      supabase.from("spiffs").select("amount_cents,paid_at,deal_id"),
      supabase.from("canvasser_payouts").select("total,status,paid_at"),
      supabase.from("team_roster").select("*"),
      supabase.rpc("financials_claims_rows"),
      supabase.rpc("financials_rs_rows"),
      supabase.from("finance_settings").select("key,value_jsonb").in("key", ["monthly_overhead", "financial_targets"]),
      extRevP,
      seriesP,
      // Real rates live in team_roster_comp (Sep 9, 2026) — the roster column
      // is neutralized. Financials viewers are leadership/finance, so the RPC
      // returns everyone. Fatal like the other legs — commission COGS must
      // not silently accrue at the 10% base.
      supabase.rpc("visible_commission_rates"),
      // Sep 25, 2026 — the stored payout ledger. Throws on error like the other
      // paged legs: paid commission cost must never silently revert to a recompute.
      fetchCommissionPayments(),
    ]);

  const firstErr = [crewQ, spiffQ, canvQ, rosterQ, claimsQ, rsQ, settingsQ, ratesQ]
    .map((q) => q.error).find(Boolean);
  if (firstErr) throw new Error(firstErr.message);

  // financials_claims_rows returns ONE jsonb array (1,063 claims already
  // exceed PostgREST's ~1,000-row cap on set-returning RPCs — storm-overlay
  // lesson: single scalar bypasses the cap). financials_rs_rows: same shape.
  const claims = (Array.isArray(claimsQ.data) ? claimsQ.data : []) as ClaimRow[];
  const rsRows = (Array.isArray(rsQ.data) ? rsQ.data : []) as RsRow[];

  const members: Record<string, TeamMember> = {};
  for (const m of (rosterQ.data ?? []) as TeamMember[]) members[m.id] = m;
  mergeRates(members, ratesMapFromRows(ratesQ.data));

  let monthlyOverhead = 0;
  let targets: FinTargets = { revenue: 0, builds: 0, margin_pct: 0 };
  for (const s of (settingsQ.data ?? []) as Array<{ key: string; value_jsonb: unknown }>) {
    if (s.key === "monthly_overhead") monthlyOverhead = num(s.value_jsonb);
    if (s.key === "financial_targets" && s.value_jsonb && typeof s.value_jsonb === "object") {
      const t = s.value_jsonb as Record<string, unknown>;
      targets = { revenue: num(t.revenue), builds: num(t.builds), margin_pct: num(t.margin_pct) };
    }
  }

  return {
    deals,
    payments,
    crew: (crewQ.data ?? []) as FinCrewPayment[],
    invoices,
    spiffs: (spiffQ.data ?? []) as FinancialsRaw["spiffs"],
    canvasserPayouts: (canvQ.data ?? []) as FinancialsRaw["canvasserPayouts"],
    members,
    commissionPayments,
    claims,
    rsRows,
    monthlyOverhead,
    targets,
    // Additive context, not core math: a failure here degrades the basis panel
    // to "not imported" rather than taking the whole board down.
    externalRevenue: (extRevQ.error ? [] : (extRevQ.data ?? [])) as ExternalRevenueRow[],
    revenueSeries: (seriesQ.error ? [] : (seriesQ.data ?? [])) as RevenueSeriesRow[],
  };
}

export async function fetchCycleStats(fromISO: string, toISO: string): Promise<CycleStat[]> {
  const { data, error } = await supabase.rpc("financials_cycle_stats", { p_from: fromISO, p_to: toISO });
  if (error) throw new Error(error.message);
  return (data ?? []) as CycleStat[];
}

// ── Periods ───────────────────────────────────────────────────────────────

export type PeriodKey = "this_month" | "last_month" | "this_quarter" | "ytd" | "t12" | "custom";

export const PERIOD_LABELS: Record<PeriodKey, string> = {
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  ytd: "YTD",
  t12: "Trailing 12",
  custom: "Custom",
};

function isoD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export interface PeriodBounds { from: string; to: string } // inclusive yyyy-mm-dd

export function periodBounds(key: PeriodKey, customFrom?: string, customTo?: string): PeriodBounds {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  switch (key) {
    case "this_month": return { from: isoD(new Date(y, m, 1)), to: isoD(now) };
    case "last_month": return { from: isoD(new Date(y, m - 1, 1)), to: isoD(new Date(y, m, 0)) };
    case "this_quarter": {
      const qm = Math.floor(m / 3) * 3;
      return { from: isoD(new Date(y, qm, 1)), to: isoD(now) };
    }
    case "ytd": return { from: `${y}-01-01`, to: isoD(now) };
    case "t12": return { from: isoD(new Date(y - 1, m, now.getDate())), to: isoD(now) };
    case "custom": {
      const f = customFrom || `${y}-01-01`;
      const t = customTo || isoD(now);
      return f <= t ? { from: f, to: t } : { from: t, to: f };
    }
  }
}

/** Same-length window immediately preceding — feeds the Δ chips. */
export function prevPeriodBounds(key: PeriodKey, b: PeriodBounds): PeriodBounds {
  if (key === "ytd") {
    const yr = Number(b.from.slice(0, 4)) - 1;
    return { from: `${yr}-01-01`, to: `${yr}${b.to.slice(4)}` };
  }
  const from = new Date(b.from + "T12:00:00");
  const to = new Date(b.to + "T12:00:00");
  const days = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
  const pf = new Date(from); pf.setDate(pf.getDate() - days);
  const pt = new Date(from); pt.setDate(pt.getDate() - 1);
  return { from: isoD(pf), to: isoD(pt) };
}

function inB(dateISO: string | null | undefined, b: PeriodBounds): boolean {
  if (!dateISO) return false;
  const d = dateISO.slice(0, 10);
  return d >= b.from && d <= b.to;
}

// ── Squares ───────────────────────────────────────────────────────────────

export function squaresForDeal(
  d: FinDeal,
  crewByDeal: Map<string, FinCrewPayment[]>,
  invByDeal: Map<string, MaterialInvoice[]>,
): number | null {
  const wb = d.pm_workbook as unknown as
    | { eagleview?: { sq_with_waste?: unknown }; post_build?: { total_square_count?: unknown } }
    | null;
  const ev = num(wb?.eagleview?.sq_with_waste);
  if (ev > 0) return ev;
  const pb = num(wb?.post_build?.total_square_count);
  if (pb > 0) return pb;
  const ts = num(d.total_squares);
  if (ts > 0) return ts;
  const crewSq = Math.max(0, ...(crewByDeal.get(d.id) ?? []).map((r) => num(r.squares)));
  if (crewSq > 0) return crewSq;
  const invSq = Math.max(0, ...(invByDeal.get(d.id) ?? []).map((r) => num(r.computed_squares)));
  if (invSq > 0) return invSq;
  return null;
}

// ── Per-deal cost maps ────────────────────────────────────────────────────

export function indexBy<T>(rows: T[], key: (r: T) => string | null | undefined): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const r of rows) {
    const k = key(r);
    if (!k) continue;
    const arr = m.get(k);
    if (arr) arr.push(r); else m.set(k, [r]);
  }
  return m;
}

const RS_POOL_RATE = 0.015; // mirrors CommissionBoard RS_POOL_RATE_LIVE

export function dealRevenue(d: FinDeal): number {
  return num(d.commissionable_revenue) || calcScope(d).commissionable;
}

/** RM/TL/ASM side vs RS-pool side — they pay on DIFFERENT flags
 *  (commission_paid vs rs_pool_paid), so cash flow times them separately.
 *  review: the 5-Star Review SPIFF on the deal (reviewSpiffByDeal) — commission
 *  cost on the job, but PAID with the weekly SPIFFs, so cash flow counts it
 *  with them (otherCompPeriod, by spiffs.paid_at), never on commission_paid. */
export interface CommSplit { rmSide: number; rs: number; review: number }

/**
 * The 5-Star Review SPIFF per deal, in dollars (Oct 19, 2026). Greg: "Count the
 * 5-star review point as commission … include it in the job's commission cost on
 * /financials and /jobs." Since Oct 19 a verified review's commission point is
 * paid as a "5-Star Review" spiffs row with deal_id set; the Tuesday review
 * booking is the only writer of spiffs.deal_id, so a SPIFF with a deal is that
 * point (Books posts it to 5400 Sales commissions on the job — migration
 * 20261019f_review_spiff_on_job). Counted from the day it is booked, paid or not
 * (commission cost is accrued); a review verified but not yet booked (until the
 * next Tuesday run) is not counted yet.
 */
export function reviewSpiffByDeal(
  spiffs: ReadonlyArray<{ deal_id?: string | null; amount_cents: number | string | null }>,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const s of spiffs) {
    if (!s.deal_id) continue;
    const dollars = num(s.amount_cents) / 100;
    if (dollars <= 0) continue;
    out.set(s.deal_id, (out.get(s.deal_id) ?? 0) + dollars);
  }
  return out;
}

/**
 * Accrued commission cost per deal: RM + split + TL + ASM lines from the
 * shared groupByPerson math (rmSide), plus the 1.5% RS pool (rs).
 * canvasserByDeal is passed empty — 0 canvasser captured-revenue rows exist
 * today (verified Aug 21), so this is exact; wire the
 * pending_canvas_appointments lookup if canvasser-sourced builds start landing.
 *
 * Sep 25, 2026: a deal paid through the stored ledger costs what was ACTUALLY
 * paid (the commission_payments rows, true-ups included, and the stored RS-pool
 * rows) — everything else stays the accrued recompute.
 *
 * Oct 19, 2026: plus the 5-Star Review SPIFF (review), from reviewSpiffByDeal.
 */
export function commissionSplitByDeal(
  deals: FinDeal[],
  members: Record<string, TeamMember>,
  payments: CommissionPaymentRow[] = [],
  canvasserByDeal: Record<string, CanvasserRef> = {},
  reviewByDeal: ReadonlyMap<string, number> = new Map(),
): Map<string, CommSplit> {
  const out = new Map<string, CommSplit>();
  const stored = indexPayments(payments);
  const groups = groupByPersonStored(deals, members, canvasserByDeal, stored);
  for (const g of groups) {
    for (const tg of g.typeGroups) {
      for (const item of tg.items) {
        const cur = out.get(item.deal.id) ?? { rmSide: 0, rs: 0, review: 0 };
        cur.rmSide += num(item.amount);
        out.set(item.deal.id, cur);
      }
    }
  }
  for (const d of deals) {
    const storedRs = storedRsPoolLines(d, stored);
    if (storedRs) {
      const cur = out.get(d.id) ?? { rmSide: 0, rs: 0, review: 0 };
      cur.rs = storedRs.reduce((s, l) => s + l.amount, 0);
      out.set(d.id, cur);
      continue;
    }
    const rev = dealRevenue(d);
    if (rev > 0) {
      const cur = out.get(d.id) ?? { rmSide: 0, rs: 0, review: 0 };
      cur.rs = rev * RS_POOL_RATE;
      out.set(d.id, cur);
    }
  }
  if (reviewByDeal.size > 0) {
    for (const d of deals) {
      const review = reviewByDeal.get(d.id) ?? 0;
      if (review <= 0) continue;
      const cur = out.get(d.id) ?? { rmSide: 0, rs: 0, review: 0 };
      cur.review = review;
      out.set(d.id, cur);
    }
  }
  return out;
}

/** Total accrued commission per deal (rmSide + rs + review) — the P&L/job-table view. */
export function commissionCostByDeal(
  deals: FinDeal[],
  members: Record<string, TeamMember>,
  payments: CommissionPaymentRow[] = [],
  reviewByDeal: ReadonlyMap<string, number> = new Map(),
): Map<string, number> {
  const out = new Map<string, number>();
  for (const [id, v] of commissionSplitByDeal(deals, members, payments, {}, reviewByDeal)) out.set(id, v.rmSide + v.rs + v.review);
  return out;
}

// ── Job cost / P&L ────────────────────────────────────────────────────────

export interface JobCost {
  deal: FinDeal;
  revenue: number;
  /**
   * Revenue split. Reps are not paid on supplements (scopeCalc backs them out
   * of `commissionable`), so the board reports them as their own line rather
   * than burying them in a single revenue figure.
   * buildRevenue + supplementRevenue === revenue, always.
   */
  buildRevenue: number;
  supplementRevenue: number;
  invoicePending: boolean;
  materials: number | null; // null = no invoice matched (never fake $0)
  materialsUnpaid: number;
  labor: number | null;
  laborUnpaid: number;
  commissions: number;
  squares: number | null;
  gross: number | null; // null when revenue unknown
  marginPct: number | null;
  commissionPaid: boolean;
}

/**
 * The revenue split, in ONE place. Both jobCost() and revenueBasis() call this
 * so the clamp cannot drift between them, and the SQL in
 * `company_build_revenue` and `fn_deal_build_value` (built branch, Oct 27,
 * 2026) mirrors it exactly — change all of them together.
 *
 * The supplement is clamped to [0, invoice] BEFORE subtracting, guarding two
 * real shapes in the data: an approved supplement larger than the invoice
 * (build floors at 0), and a NEGATIVE approved supplement — a reversed or
 * clawed-back supp, which a bare `invoice - supp` would turn into ADDED
 * revenue and double the job.
 */
export function splitRevenue(d: {
  final_invoice_amount?: number | string | null;
  supplement_approved_amount?: number | string | null;
}): { revenue: number; build: number; supplement: number } {
  const revenue = num(d.final_invoice_amount);
  const supplement = Math.max(0, Math.min(revenue, num(d.supplement_approved_amount)));
  return { revenue, build: revenue - supplement, supplement };
}

export function jobCost(
  d: FinDeal,
  crewByDeal: Map<string, FinCrewPayment[]>,
  invByDeal: Map<string, MaterialInvoice[]>,
  commByDeal: Map<string, number>,
): JobCost {
  const { revenue, build: buildRevenue, supplement: supplementRevenue } = splitRevenue(d);
  const invoicePending = revenue <= 0;
  const invs = invByDeal.get(d.id) ?? [];
  const materials = invs.length ? invs.reduce((s, r) => s + num(r.total), 0) : null;
  const materialsUnpaid = invs.reduce((s, r) => s + invoiceOwed(r), 0);
  const crewRows = (crewByDeal.get(d.id) ?? []).filter((r) => r.status === "approved" || r.status === "paid");
  const labor = crewRows.length ? crewRows.reduce((s, r) => s + num(r.total), 0) : null;
  const laborUnpaid = crewRows.filter((r) => r.status === "approved").reduce((s, r) => s + num(r.total), 0);
  const commissions = commByDeal.get(d.id) ?? 0;
  const squares = squaresForDeal(d, crewByDeal, invByDeal);
  const gross = invoicePending ? null : revenue - (materials ?? 0) - (labor ?? 0) - commissions;
  return {
    deal: d, revenue, buildRevenue, supplementRevenue,
    invoicePending, materials, materialsUnpaid, labor, laborUnpaid,
    commissions, squares,
    gross,
    marginPct: gross !== null && revenue > 0 ? (gross / revenue) * 100 : null,
    commissionPaid: d.commission_paid === true,
  };
}

export interface PnL {
  jobs: JobCost[];
  jobCount: number;
  revenue: number;
  /** revenue split — buildRevenue + supplementRevenue === revenue */
  buildRevenue: number;
  supplementRevenue: number;
  /** supplements as % of total revenue; null when there is no revenue */
  supplementPctOfRevenue: number | null;
  /**
   * Jobs in the period whose supplement outcome is still unknown (built, but
   * no supplement requested or approved yet). Supplement approval lags the
   * build by weeks, so a recent month ALWAYS understates supplement revenue —
   * surface this count rather than letting the split read as a real decline.
   */
  supplementPendingJobs: number;
  materials: number;
  materialsCoverage: number; // jobs with >=1 invoice
  labor: number;
  laborCoverage: number;
  commissions: number;
  gross: number;
  marginPct: number | null;
  /**
   * Gross profit / margin with supplement revenue stripped out. COGS is
   * identical on both sides — materials/labor are spent before the supp lands
   * and reps are not paid on supplements — so supps are pure top-line:
   * grossBeforeSupps = gross − supplementRevenue, over buildRevenue alone.
   * marginPct − marginPctBeforeSupps = the points the supplement program adds.
   */
  grossBeforeSupps: number;
  marginPctBeforeSupps: number | null;
  avgRevenue: number | null;
  avgSquares: number | null;
  sqJobs: number;
  revPerSquare: number | null;
  invoicePendingCount: number;
}

export function pnlForPeriod(
  deals: FinDeal[],
  b: PeriodBounds,
  crewByDeal: Map<string, FinCrewPayment[]>,
  invByDeal: Map<string, MaterialInvoice[]>,
  commByDeal: Map<string, number>,
): PnL {
  const built = deals.filter((d) => inB(d.build_completed_date, b));
  const jobs = built.map((d) => jobCost(d, crewByDeal, invByDeal, commByDeal));
  const revenue = jobs.reduce((s, j) => s + j.revenue, 0);
  const buildRevenue = jobs.reduce((s, j) => s + j.buildRevenue, 0);
  const supplementRevenue = jobs.reduce((s, j) => s + j.supplementRevenue, 0);
  const materials = jobs.reduce((s, j) => s + (j.materials ?? 0), 0);
  const labor = jobs.reduce((s, j) => s + (j.labor ?? 0), 0);
  const commissions = jobs.reduce((s, j) => s + j.commissions, 0);
  const gross = revenue - materials - labor - commissions;
  const grossBeforeSupps = gross - supplementRevenue;
  const sqJobs = jobs.filter((j) => (j.squares ?? 0) > 0);
  const totalSq = sqJobs.reduce((s, j) => s + (j.squares ?? 0), 0);
  const revOnSq = sqJobs.reduce((s, j) => s + j.revenue, 0);
  return {
    jobs, jobCount: jobs.length, revenue, buildRevenue, supplementRevenue,
    supplementPctOfRevenue: revenue > 0 ? (supplementRevenue / revenue) * 100 : null,
    supplementPendingJobs: jobs.filter(
      (j) => !j.invoicePending
        && num(j.deal.supplement_approved_amount) <= 0
        && num(j.deal.supplement_invoice_amount) <= 0,
    ).length,
    materials,
    materialsCoverage: jobs.filter((j) => j.materials !== null).length,
    labor,
    laborCoverage: jobs.filter((j) => j.labor !== null).length,
    commissions, gross,
    marginPct: revenue > 0 ? (gross / revenue) * 100 : null,
    grossBeforeSupps,
    marginPctBeforeSupps: buildRevenue > 0 ? (grossBeforeSupps / buildRevenue) * 100 : null,
    avgRevenue: jobs.length ? revenue / jobs.length : null,
    avgSquares: sqJobs.length ? totalSq / sqJobs.length : null,
    sqJobs: sqJobs.length,
    revPerSquare: totalSq > 0 ? revOnSq / totalSq : null,
    invoicePendingCount: jobs.filter((j) => j.invoicePending).length,
  };
}

// ── Counting metrics ──────────────────────────────────────────────────────

export function soldCount(deals: FinDeal[], b: PeriodBounds): number {
  return deals.filter((d) => inB(d.install_agreement_signed_date, b)).length;
}

export function collectedInPeriod(payments: FinPayment[], b: PeriodBounds): number {
  return payments
    .filter((p) => p.state && CLEARED_STATES.has(p.state) && inB(clearedDate(p), b))
    .reduce((s, p) => s + num(p.amount), 0);
}

// ── Backlog ───────────────────────────────────────────────────────────────

export interface BacklogStats {
  count: number;
  total: number;
  scheduled: number;
  scheduledAmt: number;
  next14: number;
  unscheduled: number;
  unscheduledAmt: number;
  stale30: number; // unscheduled, signed >30d ago
  stale60: number;
  medianAgeDays: number | null; // unscheduled, days since signing
}

export function backlogStats(deals: FinDeal[]): BacklogStats {
  const todayISO = isoD(new Date());
  const plus14 = (() => { const d = new Date(); d.setDate(d.getDate() + 14); return isoD(d); })();
  const bk = deals.filter(
    (d) => d.install_agreement_signed === true && !d.build_completed_date &&
      !TERMINAL_STAGES.has(String(d.stage ?? "")),
  );
  const contracted = (d: FinDeal) => {
    const f = num(d.final_invoice_amount);
    if (f > 0) return f;
    return Math.max(0, calcScope(d).initialInvoice);
  };
  const sched = bk.filter((d) => !!d.build_scheduled_date);
  const unsched = bk.filter((d) => !d.build_scheduled_date);
  // install_agreement_signed_date only populates from the Jul 2026 signing
  // flow onward — legacy signed deals carry just the boolean (verified: 1 of
  // 92 unscheduled had the date). Fall back to stage_entered_at so "how long
  // has this job been waiting" stays meaningful.
  const ages = unsched
    .map((d) => {
      const anchor = d.install_agreement_signed_date ?? d.stage_entered_at ?? d.created_at;
      return anchor ? Math.floor((Date.now() - new Date(anchor).getTime()) / 86400000) : null;
    })
    .filter((v): v is number => v !== null && v >= 0)
    .sort((a, z) => a - z);
  return {
    count: bk.length,
    total: bk.reduce((s, d) => s + contracted(d), 0),
    scheduled: sched.length,
    scheduledAmt: sched.reduce((s, d) => s + contracted(d), 0),
    next14: sched.filter((d) => (d.build_scheduled_date ?? "") >= todayISO && (d.build_scheduled_date ?? "") <= plus14).length,
    unscheduled: unsched.length,
    unscheduledAmt: unsched.reduce((s, d) => s + contracted(d), 0),
    stale30: ages.filter((a) => a > 30).length,
    stale60: ages.filter((a) => a > 60).length,
    medianAgeDays: ages.length ? ages[Math.floor(ages.length / 2)] : null,
  };
}

// ── Cash flow ─────────────────────────────────────────────────────────────

export interface CashFlow {
  collected: number;
  inFlight: number;
  arOutstanding: number;
  arAccounts: number;
  matPaidPeriod: number;
  crewPaidPeriod: number;
  commPaidPeriod: number;
  otherCompPeriod: number;
  matUnpaid: number;
  matUnpaidCount: number;
  matOverdue: number;
  crewOwed: number;
  crewOwedCount: number;
  commOwedApproved: number;
  commOwedApprovedCount: number;
  commPendingQueue: number;
  commPendingCount: number;
  expectedIn: number;
  owedOut: number;
  net: number;
}

/** commByDeal here is the CASH map (rmSide + rs): the 5-Star Review SPIFF is
 *  paid with the weekly SPIFFs and is already in otherCompPeriod. */
export function cashFlowStats(
  raw: FinancialsRaw,
  b: PeriodBounds,
  commByDeal: Map<string, number>,
  commSplit?: Map<string, CommSplit>,
): CashFlow {
  const rmOf = (id: string) => commSplit?.get(id)?.rmSide ?? commByDeal.get(id) ?? 0;
  const rsOf = (id: string) => commSplit?.get(id)?.rs ?? 0;
  const todayISO = isoD(new Date());
  const collected = collectedInPeriod(raw.payments, b);
  const inFlight = raw.payments
    .filter((p) => p.state && IN_FLIGHT_STATES.has(p.state))
    .reduce((s, p) => s + num(p.amount), 0);
  const arDeals = raw.deals.filter(
    (d) => (d.stage === "collecting" || d.stage === "delinquent") && num(d.post_build_balance_remaining) > 0,
  );
  const arOutstanding = arDeals.reduce((s, d) => s + num(d.post_build_balance_remaining), 0);

  const matPaidPeriod = raw.invoices
    .filter((i) => i.paid_status === "paid" && inB(i.paid_at, b))
    .reduce((s, i) => s + num(i.total), 0);
  const crewPaidPeriod = raw.crew
    .filter((c) => c.status === "paid" && inB(c.paid_at, b))
    .reduce((s, c) => s + num(c.total), 0);
  // RM/TL/ASM side pays on commission_paid; the RS pool pays on rs_pool_paid —
  // time each by its own flag (audit finding: lumping them mis-stated both
  // "paid this period" and "owed").
  const commPaidPeriod =
    raw.deals.filter((d) => d.commission_paid === true && inB(d.commission_paid_at, b))
      .reduce((s, d) => s + rmOf(d.id), 0) +
    raw.deals.filter((d) => d.rs_pool_paid === true && inB(d.rs_pool_paid_at ?? null, b))
      .reduce((s, d) => s + rsOf(d.id), 0);
  const otherCompPeriod =
    raw.spiffs.filter((s2) => inB(s2.paid_at, b)).reduce((s, r) => s + num(r.amount_cents) / 100, 0) +
    raw.canvasserPayouts.filter((c) => c.status === "paid" && inB(c.paid_at, b)).reduce((s, c) => s + num(c.total), 0);

  const matUnpaidRows = raw.invoices.filter((i) => i.paid_status === "unpaid");
  const matUnpaid = matUnpaidRows.reduce((s, i) => s + invoiceOwed(i), 0);
  const matOverdue = matUnpaidRows
    .filter((i) => (i.due_date ?? "9999") < todayISO)
    .reduce((s, i) => s + invoiceOwed(i), 0);
  const crewOwedRows = raw.crew.filter((c) => c.status === "approved" && !c.paid_at);
  const crewOwed = crewOwedRows.reduce((s, c) => s + num(c.total), 0);
  const commApprovedUnpaid = raw.deals.filter(
    (d) => !!d.commission_ready_date && d.commission_approved === true && d.commission_paid !== true,
  );
  const commOwedApproved =
    commApprovedUnpaid.reduce((s, d) => s + rmOf(d.id), 0) +
    raw.deals.filter((d) => !!d.commission_ready_date && d.rs_pool_approved === true && d.rs_pool_paid !== true)
      .reduce((s, d) => s + rsOf(d.id), 0);
  const commPendingDeals = raw.deals.filter(
    (d) => !!d.commission_ready_date && d.commission_approved !== true && d.commission_paid !== true,
  );
  const commPendingQueue =
    commPendingDeals.reduce((s, d) => s + rmOf(d.id), 0) +
    raw.deals.filter((d) => !!d.commission_ready_date && d.rs_pool_approved !== true && d.rs_pool_paid !== true)
      .reduce((s, d) => s + rsOf(d.id), 0);

  const expectedIn = inFlight + arOutstanding;
  const owedOut = matUnpaid + crewOwed + commOwedApproved + commPendingQueue;
  return {
    collected, inFlight, arOutstanding, arAccounts: arDeals.length,
    matPaidPeriod, crewPaidPeriod, commPaidPeriod, otherCompPeriod,
    matUnpaid, matUnpaidCount: matUnpaidRows.length, matOverdue,
    crewOwed, crewOwedCount: crewOwedRows.length,
    commOwedApproved, commOwedApprovedCount: commApprovedUnpaid.length,
    commPendingQueue, commPendingCount: commPendingDeals.length,
    expectedIn, owedOut, net: expectedIn - owedOut,
  };
}

// ── Monthly trend ─────────────────────────────────────────────────────────

export interface MonthRow {
  ym: string;
  label: string;
  jobs: number;
  revenue: number;
  buildRevenue: number;
  supplementRevenue: number;
  cogs: number; // materials + labor + commissions on jobs with revenue
  marginPct: number | null;
  matPerSq: number | null; // materials $ / squares on measured jobs
  partial: boolean;
  /** Same month, prior year. null when that month predates our build history. */
  prevRevenue: number | null;
  prevJobs: number | null;
  /** Revenue % change vs prior-year same month. null when prevRevenue is null/0. */
  yoyPct: number | null;
  /**
   * For the in-progress month we clip the prior-year window to the same day
   * of month, so a partial Aug never reads as a collapse against a full Aug.
   * Holds that clipped end date (YYYY-MM-DD) for the tooltip; null otherwise.
   */
  prevClippedTo: string | null;
}

export function monthlyTrend(
  deals: FinDeal[],
  crewByDeal: Map<string, FinCrewPayment[]>,
  invByDeal: Map<string, MaterialInvoice[]>,
  commByDeal: Map<string, number>,
  months = 12,
): MonthRow[] {
  const now = new Date();
  const today = now.getDate();

  // One pass of job costing per window, reused by the current and prior-year legs.
  const agg = (b: PeriodBounds) => {
    const jobs = deals.filter((d) => inB(d.build_completed_date, b))
      .map((d) => jobCost(d, crewByDeal, invByDeal, commByDeal));
    const revenue = jobs.reduce((s, j) => s + j.revenue, 0);
    const buildRevenue = jobs.reduce((s, j) => s + j.buildRevenue, 0);
    const supplementRevenue = jobs.reduce((s, j) => s + j.supplementRevenue, 0);
    const cogs = jobs.reduce((s, j) => s + (j.materials ?? 0) + (j.labor ?? 0) + j.commissions, 0);
    // Margin over INVOICED jobs only — fresh builds accrue material/labor
    // costs days before the invoice is entered, which made a partial month
    // read a nonsense negative margin (caught in headless verification).
    const invoiced = jobs.filter((j) => !j.invoicePending);
    const invRev = invoiced.reduce((s, j) => s + j.revenue, 0);
    const invCogs = invoiced.reduce((s, j) => s + (j.materials ?? 0) + (j.labor ?? 0) + j.commissions, 0);
    const sqJobs = jobs.filter((j) => (j.squares ?? 0) > 0 && j.materials !== null);
    const sq = sqJobs.reduce((s, j) => s + (j.squares ?? 0), 0);
    const matOnSq = sqJobs.reduce((s, j) => s + (j.materials ?? 0), 0);
    return {
      count: jobs.length,
      revenue,
      buildRevenue,
      supplementRevenue,
      cogs,
      marginPct: invRev > 0 ? ((invRev - invCogs) / invRev) * 100 : null,
      matPerSq: sq > 0 ? matOnSq / sq : null,
    };
  };

  const out: MonthRow[] = [];
  for (let i = months - 1; i >= 0; i--) {
    const d0 = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const d1 = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
    const partial = i === 0;
    const cur = agg({ from: isoD(d0), to: isoD(d1) });

    // Prior-year same month. For the in-progress month, clip the comparison
    // window to the same day count so we compare like with like — otherwise a
    // month-to-date bar always reads as a double-digit YoY collapse.
    const p0 = new Date(d0.getFullYear() - 1, d0.getMonth(), 1);
    const pLast = new Date(d0.getFullYear() - 1, d0.getMonth() + 1, 0);
    const p1 = partial
      ? new Date(d0.getFullYear() - 1, d0.getMonth(), Math.min(today, pLast.getDate()))
      : pLast;
    const prev = agg({ from: isoD(p0), to: isoD(p1) });

    // No jobs at all in the prior-year window means we have no history there,
    // not a 100% decline. Leave it blank rather than print a fake cliff.
    const hasPrev = prev.count > 0;

    out.push({
      ym: isoD(d0).slice(0, 7),
      label: d0.toLocaleDateString("en-US", { month: "short" }),
      jobs: cur.count,
      revenue: cur.revenue,
      buildRevenue: cur.buildRevenue,
      supplementRevenue: cur.supplementRevenue,
      cogs: cur.cogs,
      marginPct: cur.marginPct,
      matPerSq: cur.matPerSq,
      partial,
      prevRevenue: hasPrev ? prev.revenue : null,
      prevJobs: hasPrev ? prev.count : null,
      yoyPct: hasPrev && prev.revenue > 0
        ? ((cur.revenue - prev.revenue) / prev.revenue) * 100
        : null,
      prevClippedTo: partial && hasPrev ? isoD(p1) : null,
    });
  }
  return out;
}

// ── Breakdowns ────────────────────────────────────────────────────────────

export type BreakdownDim = "carrier" | "zip" | "city" | "rm" | "tl" | "asm" | "rs" | "lead_source";

export const BREAKDOWN_LABELS: Record<BreakdownDim, string> = {
  carrier: "Insurance carrier", zip: "Zip code", city: "City",
  rm: "RM", tl: "Team (TL)", asm: "ASM", rs: "Roof specialist", lead_source: "Lead source",
};

export interface BreakdownRow {
  key: string;
  name: string;
  jobs: number;
  revenue: number;
  buildRevenue: number;
  supplementRevenue: number;
  avg: number | null;
  share: number; // 0..1 of period revenue
  gross: number | null; // null when cost coverage incomplete for the group
  concentration: boolean;
}

export function carrierDisplay(raw: string): string {
  const k = raw.trim().toLowerCase();
  if (!k) return "(not recorded)";
  if (k === "usaa") return "USAA";
  if (k === "aaa") return "AAA";
  return k.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function breakdownRows(
  dim: BreakdownDim,
  pnl: PnL,
  members: Record<string, TeamMember>,
): BreakdownRow[] {
  // Person dims split revenue ½/½ on split deals (leaderboard convention).
  type Slice = {
    key: string; name: string; jobs: number;
    revenue: number; build: number; supp: number; gross: number | null;
  };
  const acc = new Map<string, Slice>();
  const add = (
    key: string, name: string, jobs: number,
    revenue: number, build: number, supp: number, gross: number | null,
  ) => {
    const cur = acc.get(key)
      ?? { key, name, jobs: 0, revenue: 0, build: 0, supp: 0, gross: 0 as number | null };
    cur.jobs += jobs; cur.revenue += revenue; cur.build += build; cur.supp += supp;
    cur.gross = cur.gross === null || gross === null ? null : cur.gross + gross;
    acc.set(key, cur);
  };
  const tlOf = (personId: string | null | undefined): string => {
    const m = personId ? members[personId] : undefined;
    if (!m) return "__none";
    if (m.role === "team_lead") return m.id;
    if (m.role === "restore_manager" && m.team_lead_id) return m.team_lead_id;
    return "__none";
  };
  const asmOf = (personId: string | null | undefined): string => {
    const m = personId ? members[personId] : undefined;
    if (!m) return "__none";
    if (m.role === "asm") return m.id;
    if (m.asm_id) return m.asm_id;
    if (m.role === "restore_manager" && m.team_lead_id) {
      const tl = members[m.team_lead_id];
      if (tl?.asm_id) return tl.asm_id;
    }
    return "__none";
  };
  for (const j of pnl.jobs) {
    const d = j.deal;
    if (dim === "carrier") {
      const k = String(d.insurance_company ?? "").trim().toLowerCase();
      add(k || "__none", carrierDisplay(k), 1, j.revenue, j.buildRevenue, j.supplementRevenue, j.gross);
    } else if (dim === "zip") {
      const z = String(d.property_zip ?? "").slice(0, 5);
      add(z || "__none", z ? `${z} · ${d.property_city ?? ""}` : "(no zip)", 1, j.revenue, j.buildRevenue, j.supplementRevenue, j.gross);
    } else if (dim === "city") {
      const c = String(d.property_city ?? "").trim();
      add(c.toLowerCase() || "__none", c || "(no city)", 1, j.revenue, j.buildRevenue, j.supplementRevenue, j.gross);
    } else if (dim === "lead_source") {
      const c = String(d.lead_source ?? "").trim();
      add(c.toLowerCase() || "__none", c || "(not recorded)", 1, j.revenue, j.buildRevenue, j.supplementRevenue, j.gross);
    } else if (dim === "rs") {
      // One RS per deal (assigned_rs_id) — no split-halving, unlike RM/TL/ASM.
      const pid = d.assigned_rs_id ?? null;
      add(pid ?? "__none", pid ? (members[pid]?.full_name ?? "Unknown") : "(no RS assigned)",
        1, j.revenue, j.buildRevenue, j.supplementRevenue, j.gross);
    } else {
      // person dims — halve on splits
      const isSplit = !!d.split_rm_id;
      const parts: Array<[string | null | undefined, number]> = isSplit
        ? [[d.owner_id, 0.5], [d.split_rm_id, 0.5]]
        : [[d.owner_id, 1]];
      for (const [pid, share] of parts) {
        let key: string, name: string;
        if (dim === "rm") {
          key = pid ?? "__none";
          name = pid ? (members[pid]?.full_name ?? "Unknown") : "(no owner)";
        } else if (dim === "tl") {
          key = tlOf(pid);
          name = key === "__none" ? "Direct / no TL" : `Team ${members[key]?.full_name?.split(" ").slice(-1)[0] ?? "?"}`;
        } else {
          key = asmOf(pid);
          name = key === "__none" ? "No ASM" : (members[key]?.full_name ?? "Unknown");
        }
        add(key, name, share, j.revenue * share, j.buildRevenue * share, j.supplementRevenue * share,
          j.gross === null ? null : j.gross * share);
      }
    }
  }
  const total = pnl.revenue || 1;
  const rows = [...acc.values()]
    .sort((a, z) => z.revenue - a.revenue)
    .map((r) => ({
      key: r.key, name: r.name,
      jobs: Math.round(r.jobs * 10) / 10,
      revenue: r.revenue,
      buildRevenue: r.build,
      supplementRevenue: r.supp,
      avg: r.jobs > 0 ? r.revenue / r.jobs : null,
      share: r.revenue / total,
      gross: r.gross,
      concentration: r.revenue / total >= 0.25 && acc.size > 1 && r.key !== "__none",
    }));
  return rows;
}

// ── Supplement capture ────────────────────────────────────────────────────

export interface SuppCapture {
  jobsWithSupps: number;
  requested: number;
  approved: number;
  capturePct: number | null;
  suppPctOfRevenue: number | null;
  byCarrier: Array<{ name: string; requested: number; approved: number; pct: number | null; n: number }>;
}

export function suppCapture(pnl: PnL): SuppCapture {
  const rows = pnl.jobs.filter((j) => num(j.deal.supplement_invoice_amount) > 0);
  const requested = rows.reduce((s, j) => s + num(j.deal.supplement_invoice_amount), 0);
  const approved = rows.reduce((s, j) => s + num(j.deal.supplement_approved_amount), 0);
  const byC = new Map<string, { requested: number; approved: number; n: number }>();
  for (const j of rows) {
    const k = String(j.deal.insurance_company ?? "").trim().toLowerCase() || "__none";
    const cur = byC.get(k) ?? { requested: 0, approved: 0, n: 0 };
    cur.requested += num(j.deal.supplement_invoice_amount);
    cur.approved += num(j.deal.supplement_approved_amount);
    cur.n += 1;
    byC.set(k, cur);
  }
  return {
    jobsWithSupps: rows.length,
    requested, approved,
    capturePct: requested > 0 ? (approved / requested) * 100 : null,
    suppPctOfRevenue: pnl.revenue > 0 ? (approved / pnl.revenue) * 100 : null,
    byCarrier: [...byC.entries()]
      .map(([k, v]) => ({
        name: carrierDisplay(k === "__none" ? "" : k),
        requested: v.requested, approved: v.approved,
        pct: v.requested > 0 ? (v.approved / v.requested) * 100 : null,
        n: v.n,
      }))
      .sort((a, z) => z.requested - a.requested)
      .slice(0, 8),
  };
}

// ── Claims lens ───────────────────────────────────────────────────────────

export interface ClaimsAgg { c: number; ap: number; de: number; lo: number; bu: number; op: number; dec: number }

export function claimsAgg(rows: ClaimRow[]): ClaimsAgg {
  const a = { c: rows.length, ap: 0, de: 0, lo: 0, bu: 0, op: 0, dec: 0 };
  for (const r of rows) {
    if (r.approved) a.ap++;
    else if (r.denied) a.de++;
    else if (r.lost) a.lo++;
    if (r.built) a.bu++;
  }
  a.op = a.c - a.ap - a.de - a.lo;
  a.dec = a.ap + a.de;
  return a;
}

export function claimsFilter(
  rows: ClaimRow[],
  carrier: string,
  zip: string,
  from: string,
  to: string,
  /** true = the "(not recorded)" bucket — carrier key is literally "" there,
   *  which would otherwise be indistinguishable from "all carriers" */
  emptyCarrierOnly = false,
): ClaimRow[] {
  return rows.filter(
    (r) =>
      (emptyCarrierOnly ? r.carrier === "" : (!carrier || r.carrier === carrier)) &&
      (!zip || r.zip === zip) && r.filed >= from && r.filed <= to,
  );
}

// ── Roof specialist performance ───────────────────────────────────────────

export interface RsPerfRow {
  key: string; // person id, or "__none"
  name: string;
  preInspections: number; // completed climbs credited to this person, by climb date
  meetings: number; // 1st adjuster meetings the RS ran in the period
  reinspMeetings: number; // 2nd-adjuster (reinspection) meetings in the period
  approved: number;
  denied: number;
  lost: number; // closed before a decision — never counts against approval
  open: number;
  decided: number; // approved + denied
  approvalPct: number | null; // approved ÷ decided
  /**
   * The pre-inspected slice of the outcome claims — THE numbers the table
   * displays (Jennifer's call, Sep 4: grade an RS only on claims whose roof
   * was actually climbed). The blended fields above survive for the
   * with-vs-without summary; they are not shown per row.
   */
  preApproved: number;
  preDenied: number;
  preLost: number;
  preOpen: number;
  preDecided: number;
  preApprovalPct: number | null;
  preReinspected: number;
  preReinspWon: number;
  reinspected: number; // of ALL outcome claims, how many took the reinspection path
  reinspWon: number; // reinspected claims that ended approved — the fight paid off
  builtJobs: number; // builds COMPLETED in the period (same basis as the P&L)
  builtRevenue: number;
}

export interface PreApprovalSummary {
  withPre: { approved: number; decided: number; pct: number | null };
  withoutPre: { approved: number; decided: number; pct: number | null };
}

/**
 * Company-wide pre-inspected vs not, for the same period the table shows.
 * Safe to sum across rows: each deal's outcome lands in exactly ONE row
 * (keyed by assigned RS), so there is no double counting. "Without" means
 * without a RECORDED climb — under-recording (PRE Done not clicked) moves
 * real climbs into that bucket, which UNDERSTATES the true spread.
 */
export function rsPreApprovalSummary(rows: RsPerfRow[]): PreApprovalSummary {
  let pAp = 0, pDe = 0, ap = 0, de = 0;
  for (const r of rows) {
    pAp += r.preApproved; pDe += r.preDecided;
    ap += r.approved; de += r.decided;
  }
  const nAp = ap - pAp, nDe = de - pDe;
  return {
    withPre: { approved: pAp, decided: pDe, pct: pDe > 0 ? (pAp / pDe) * 100 : null },
    withoutPre: { approved: nAp, decided: nDe, pct: nDe > 0 ? (nAp / nDe) * 100 : null },
  };
}

/**
 * Per-RS activity + outcomes for the period. Three anchors, deliberately:
 *   preInspections — by CLIMB date (completed date, else the meeting the climb
 *     served), credited to the CLIMBER — who can differ from the meeting RS,
 *     so one deal can legitimately land in two people's rows.
 *   meetings/outcomes — by MEETING date (the RS's own work in the window;
 *     outcome = the claim's CURRENT status, so recent meetings sit in Open).
 *   builtJobs/builtRevenue — by build-completion date via pnl.jobs, so the
 *     revenue column reconciles with the P&L and the Breakdowns bars above.
 * A deal with both meetings in the window counts its outcome ONCE (dedupe by
 * deal_id); meetings and reinspMeetings each count their own leg.
 *
 * carrierKey filters EVERY leg (climbs, meetings, outcomes, built) to one
 * carrier: "" / undefined = all; "__none" = carrier not recorded (a real key —
 * same sentinel convention as the claims lens); else the normalized key.
 */
export function rsPerformance(
  rsRows: RsRow[],
  pnl: PnL,
  b: PeriodBounds,
  members: Record<string, TeamMember>,
  carrierKey = "",
): RsPerfRow[] {
  const matchCarrier = (k: string): boolean =>
    !carrierKey ? true : carrierKey === "__none" ? k === "" : k === carrierKey;
  type Acc = {
    preInsp: number; meetings: number; reinspMtgs: number;
    outcomes: Map<string, RsRow>;
    builtJobs: number; builtRevenue: number;
  };
  const acc = new Map<string, Acc>();
  const get = (key: string): Acc => {
    const cur = acc.get(key);
    if (cur) return cur;
    const fresh: Acc = { preInsp: 0, meetings: 0, reinspMtgs: 0, outcomes: new Map(), builtJobs: 0, builtRevenue: 0 };
    acc.set(key, fresh);
    return fresh;
  };
  for (const r of rsRows) {
    if (!matchCarrier(r.carrier ?? "")) continue;
    const metIn = inB(r.met, b);
    const met2In = inB(r.met2, b);
    if (metIn || met2In) {
      const a = get(r.rs ?? "__none");
      if (metIn) a.meetings += 1;
      if (met2In) a.reinspMtgs += 1;
      a.outcomes.set(r.deal_id, r);
    }
    // Climbs credit the climber, not the meeting RS, on their own anchor.
    if (r.pre_done && inB(r.pre_on, b)) get(r.pre_by ?? "__none").preInsp += 1;
  }
  for (const j of pnl.jobs) {
    if (!matchCarrier(String(j.deal.insurance_company ?? "").trim().toLowerCase())) continue;
    const a = get(j.deal.assigned_rs_id ?? "__none");
    a.builtJobs += 1;
    a.builtRevenue += j.revenue;
  }
  const rows = [...acc.entries()].map(([key, a]) => {
    let ap = 0, de = 0, lo = 0, ri = 0, rw = 0;
    let pN = 0, pAp = 0, pDe = 0, pLo = 0, pRi = 0, pRw = 0;
    for (const r of a.outcomes.values()) {
      const pre = r.pre_done;
      if (pre) pN++;
      if (r.approved) { ap++; if (pre) pAp++; }
      else if (r.denied) { de++; if (pre) pDe++; }
      else if (r.lost) { lo++; if (pre) pLo++; }
      if (r.reinsp) {
        ri++; if (r.approved) rw++;
        if (pre) { pRi++; if (r.approved) pRw++; }
      }
    }
    const open = a.outcomes.size - ap - de - lo;
    const decided = ap + de;
    const preDecided = pAp + pDe;
    return {
      key,
      name: key === "__none" ? "(unassigned)" : (members[key]?.full_name ?? "Unknown"),
      preInspections: a.preInsp,
      meetings: a.meetings, reinspMeetings: a.reinspMtgs,
      approved: ap, denied: de, lost: lo, open, decided,
      approvalPct: decided > 0 ? (ap / decided) * 100 : null,
      preApproved: pAp, preDenied: pDe, preLost: pLo,
      preOpen: pN - pAp - pDe - pLo,
      preDecided,
      preApprovalPct: preDecided > 0 ? (pAp / preDecided) * 100 : null,
      preReinspected: pRi, preReinspWon: pRw,
      reinspected: ri, reinspWon: rw,
      builtJobs: a.builtJobs, builtRevenue: a.builtRevenue,
    };
  });
  // People first (busiest on top); the unassigned bucket is a data-quality
  // note, not a person — it sits last regardless of volume.
  rows.sort((a, z) => {
    if ((a.key === "__none") !== (z.key === "__none")) return a.key === "__none" ? 1 : -1;
    return z.meetings - a.meetings || z.preInspections - a.preInspections || z.builtRevenue - a.builtRevenue;
  });
  return rows;
}

// ── 13-week cash forecast ─────────────────────────────────────────────────

export interface ForecastWeek {
  start: string; // Monday ISO
  label: string;
  inflow: number;
  outflow: number;
  net: number;
  cum: number;
}

export interface Forecast {
  weeks: ForecastWeek[];
  assumptions: string[];
}

/**
 * Rolling 13-week projection. Deliberately simple and labeled — it "builds
 * itself out": collection lag comes from financials_cycle_stats when n≥10,
 * materials $/sq from actual invoice history when present.
 * commByDeal is the CASH map (rmSide + rs), timed by the commission flags; the
 * 5-Star Review SPIFF is paid with the SPIFFs, which this does not project.
 */
export function forecast13w(
  raw: FinancialsRaw,
  commByDeal: Map<string, number>,
  cycle: CycleStat[],
  crewByDeal: Map<string, FinCrewPayment[]>,
  invByDeal: Map<string, MaterialInvoice[]>,
): Forecast {
  const assumptions: string[] = [];
  const today = new Date();
  const monday = new Date(today);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const weekStarts: string[] = [];
  for (let i = 0; i < 13; i++) {
    const d = new Date(monday); d.setDate(d.getDate() + i * 7);
    weekStarts.push(isoD(d));
  }
  const weekIdx = (dateISO: string): number => {
    const t = new Date(dateISO + "T12:00:00").getTime();
    const w0 = new Date(weekStarts[0] + "T12:00:00").getTime();
    const idx = Math.floor((t - w0) / (7 * 86400000));
    return idx; // may be <0 (overdue) or >12 (beyond)
  };
  const inflow = new Array(13).fill(0);
  const outflow = new Array(13).fill(0);
  const clamp = (i: number) => Math.max(0, Math.min(12, i));

  const buildToPaid = cycle.find((c) => c.metric === "build_to_paid");
  const payLagDays = buildToPaid && buildToPaid.n >= 10 && buildToPaid.median_days ? Math.round(num(buildToPaid.median_days)) : 46;
  assumptions.push(
    buildToPaid && buildToPaid.n >= 10
      ? `Collections land ~${payLagDays} days after build (your median, n=${buildToPaid.n})`
      : `Collections land ~${payLagDays} days after build (default until enough history)`,
  );

  // IN — in-flight settles over the next 2 weeks
  const inFlightTotal = raw.payments
    .filter((p) => p.state && IN_FLIGHT_STATES.has(p.state))
    .reduce((s, p) => s + num(p.amount), 0);
  inflow[0] += inFlightTotal * 0.5;
  inflow[1] += inFlightTotal * 0.5;
  assumptions.push("In-flight payments (checks at iink, ACH settling) split across the next 2 weeks");

  // IN — AR: expected at build date + lag; overdue folds into weeks 1-4
  for (const d of raw.deals) {
    if (!(d.stage === "collecting" || d.stage === "delinquent")) continue;
    const bal = num(d.post_build_balance_remaining);
    if (bal <= 0) continue;
    if (d.build_completed_date) {
      const due = new Date(d.build_completed_date + "T12:00:00");
      due.setDate(due.getDate() + payLagDays);
      const idx = weekIdx(isoD(due));
      if (idx < 0) { for (let k = 0; k < 4; k++) inflow[k] += bal / 4; }
      else if (idx <= 12) inflow[idx] += bal;
      else inflow[12] += bal;
    } else {
      for (let k = 0; k < 4; k++) inflow[k] += bal / 4;
    }
  }
  assumptions.push("Overdue AR spread over the next 4 weeks (call list, not a guarantee)");

  // materials $/sq from history (matched invoices vs squares), else default
  let histMat = 0, histSq = 0;
  for (const d of raw.deals) {
    if (!d.build_completed_date) continue;
    const invs = invByDeal.get(d.id);
    if (!invs?.length) continue;
    const sq = squaresForDeal(d, crewByDeal, invByDeal);
    if (!sq || sq <= 0) continue;
    histMat += invs.reduce((s, r) => s + num(r.total), 0);
    histSq += sq;
  }
  const matPerSq = histSq > 50 ? histMat / histSq : 175;
  assumptions.push(
    histSq > 50
      ? `Future builds priced at $${Math.round(matPerSq)}/sq materials (your actual average)`
      : "Future builds priced at $175/sq materials (default until invoices accumulate)",
  );

  // Scheduled builds: deposit lands build week; dep settlement at +lag;
  // materials invoice due ~4 weeks after build; labor paid ~1 week after.
  const todayISO = isoD(new Date());
  for (const d of raw.deals) {
    if (!d.build_scheduled_date || d.build_completed_date) continue;
    if (TERMINAL_STAGES.has(String(d.stage ?? ""))) continue;
    if (d.build_scheduled_date < todayISO) continue;
    const bIdx = weekIdx(d.build_scheduled_date);
    if (bIdx > 12) continue;
    const scope = calcScope(d);
    const sq = squaresForDeal(d, crewByDeal, invByDeal) ?? 0;
    inflow[clamp(bIdx)] += Math.max(0, scope.firstPaymentToBardin);
    const depIdx = bIdx + Math.round(payLagDays / 7);
    // Oct 29, 2026: what comes in after the build is the completion payment — the
    // depreciation check plus the trades billed to the homeowner (due at
    // completion), less any credit beyond the deductible. Retail: the other half
    // of the contract + the trades (the same number depSettlement held there).
    if (depIdx <= 12) inflow[clamp(depIdx)] += Math.max(0, scope.completionPayment);
    if (sq > 0) {
      outflow[clamp(bIdx + 4)] += sq * matPerSq;
      outflow[clamp(bIdx + 1)] += sq * (sq >= 60 ? 100 : 90) + 350; // Risol rate card
    }
    const comm = commByDeal.get(d.id) ?? 0;
    if (comm > 0) outflow[clamp(bIdx + 2)] += comm;
  }
  assumptions.push("Each scheduled build: deposit at build · materials due +4wk · crew paid +1wk · commissions +2wk");

  // Known payables
  for (const i of raw.invoices) {
    if (i.paid_status !== "unpaid") continue;
    const idx = i.due_date ? weekIdx(i.due_date) : 0;
    outflow[clamp(idx)] += invoiceOwed(i);
  }
  for (const c of raw.crew) {
    if (c.status === "approved" && !c.paid_at) outflow[0] += num(c.total);
  }
  for (const d of raw.deals) {
    if (!!d.commission_ready_date && d.commission_paid !== true) {
      const comm = commByDeal.get(d.id) ?? 0;
      outflow[d.commission_approved ? 0 : 1] += comm;
    }
  }
  assumptions.push("Unpaid SRS invoices land on their due dates; approved crew + commissions in the next 2 weeks");

  let cum = 0;
  const weeks = weekStarts.map((ws, i) => {
    const net = inflow[i] - outflow[i];
    cum += net;
    const d = new Date(ws + "T12:00:00");
    return {
      start: ws,
      label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      inflow: inflow[i], outflow: outflow[i], net, cum,
    };
  });
  return { weeks, assumptions };
}

// ── Targets / deltas ──────────────────────────────────────────────────────

export interface Delta { pct: number | null; up: boolean }

export function deltaOf(cur: number, prev: number): Delta {
  if (!prev) return { pct: null, up: cur >= 0 };
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  return { pct, up: pct >= 0 };
}

/** Scale a monthly target to an arbitrary period length. */
export function targetForPeriod(monthlyTarget: number, b: PeriodBounds): number | null {
  if (!monthlyTarget) return null;
  const from = new Date(b.from + "T12:00:00");
  const to = new Date(b.to + "T12:00:00");
  const days = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
  return (monthlyTarget * days) / 30.44;
}


// ── Revenue basis comparison ──────────────────────────────────────────────
//
// Two honest answers to "what was revenue this year", kept side by side:
//   booked  — QuickBooks, recognized when INVOICED. What Greg has always used.
//   built   — this board, recognized when the ROOF IS COMPLETED.
// Neither is wrong; they answer different questions. They converge over a full
// year and diverge inside one (deposits on unbuilt work, prior-year collections,
// catch-up invoicing batches).

export interface BasisYear {
  year: string;
  booked: number | null;      // QuickBooks, null when not imported. NOT splittable:
                              // the P&L by Month exports carry one `Services`
                              // income account, so supplements are not broken
                              // out on the booked side at source.
  built: number;              // CRM build-completion (build + supplements)
  builtBuild: number;         // build revenue only — the commissionable basis
  builtSupplement: number;    // approved supplements
  supplementPct: number | null; // supplements as % of built total
  builds: number;
  unbilledBuilds: number;
  variance: number | null;    // built − booked
  variancePct: number | null;
  /** CRM build history is thin before 2025 — the built figure is not comparable. */
  crmCoverageThin: boolean;
  /** Zoho production for years the CRM cannot answer. Initial invoice dated by
   *  preferred build date — validated against 2025, where it tracks CRM build
   *  revenue within a few percent month by month. */
  /** Supplement portion of booked revenue, from the QBO transaction detail. */
  /** Official revenue for the year, and its supplement split. */
  officialRevenue: number | null;
  officialSupplements: number | null;
  officialBuildRevenue: number | null;
  /** Which system(s) each figure came from, e.g. ["quickbooks","crm"]. */
  officialRevenueSources: string[];
  officialSupplementSources: string[];
  bookedSupplement: number | null;
  /** Months inside this year with no supplement split imported — the booked
   *  supplement figure understates by exactly those months. */
  bookedSupplementGapMonths: number;
  /** Months where QBO itemized supplements. A year mixing itemized and
   *  non-itemized months cannot have its supplement RATE compared to a fully
   *  itemized year — the denominator includes revenue with no split. */
  supplementItemizedMonths: number;
  supplementNotItemizedMonths: number;
  /** Booked revenue from the itemized months only — the honest denominator. */
  bookedItemizedPortion: number;
  /** Invoices closed with the open balance written off (QBO credit memos). */
  badDebt: number | null;
  lateFee: number | null;
  /** The CRM's own small-residual write-off field, for the same year. */
  crmWrittenOff: number;
  /** Non-collect revenue — amounts deliberately billed but not pursued, per the
   *  approved process. This is what QuickBooks books as a Bad Debt credit memo
   *  against a GROSS invoice; the CRM instead nets it out of final_invoice_amount
   *  up front. Same outcome, two routes — so QBO bad debt ≈ CRM non-collect. */
  nonCollect: number;
  legacyProduction: number | null;
  legacyDeals: number | null;
  /** true when the production figure shown came from Zoho, not the CRM. */
  productionIsLegacy: boolean;
  /** Best available production figure: CRM when it has coverage, else Zoho. */
  production: number | null;
  productionBuilds: number | null;
  /** Year still in progress; booked side may also be a partial month. */
  inProgress: boolean;
  bookedPartial: boolean;
}

export function revenueBasis(
  deals: FinDeal[],
  external: ExternalRevenueRow[],
  series: RevenueSeriesRow[] = [],
): BasisYear[] {
  // The official series, per Greg: QuickBooks revenue and supplements through
  // 2025; for 2026 QuickBooks until it stopped, then the CRM.
  const off = new Map<string, { rev: number; supp: number; srcRev: Set<string>; srcSupp: Set<string> }>();
  for (const r of series) {
    const y = String(r.ym ?? "").slice(0, 4);
    if (!/^\d{4}$/.test(y)) continue;
    const cur = off.get(y) ?? { rev: 0, supp: 0, srcRev: new Set<string>(), srcSupp: new Set<string>() };
    cur.rev += num(r.revenue); cur.supp += num(r.supplements);
    if (r.revenue_source) cur.srcRev.add(r.revenue_source);
    if (r.supplement_source) cur.srcSupp.add(r.supplement_source);
    off.set(y, cur);
  }
  const thisYear = String(new Date().getFullYear());

  const booked = new Map<string, {
    rev: number; supp: number; suppGap: number; partial: boolean;
    itemMonths: number; nonItemMonths: number; itemRev: number;
    badDebt: number; lateFee: number; feeGap: number;
  }>();
  const legacy = new Map<string, { rev: number; n: number }>();
  for (const r of external) {
    const y = String(r.ym ?? "").slice(0, 4);
    if (!/^\d{4}$/.test(y)) continue;
    if (r.basis === "production_legacy") {
      const cur = legacy.get(y) ?? { rev: 0, n: 0 };
      cur.rev += num(r.revenue);
      cur.n += Number(r.deal_count ?? 0);
      legacy.set(y, cur);
      continue;
    }
    const cur = booked.get(y) ?? {
      rev: 0, supp: 0, suppGap: 0, partial: false,
      itemMonths: 0, nonItemMonths: 0, itemRev: 0, badDebt: 0, lateFee: 0, feeGap: 0,
    };
    cur.rev += num(r.revenue);
    if (r.supplement_itemized) { cur.itemMonths += 1; cur.itemRev += num(r.revenue); }
    else cur.nonItemMonths += 1;
    if (r.bad_debt == null) cur.feeGap += 1;
    else { cur.badDebt += num(r.bad_debt); cur.lateFee += num(r.late_fee); }
    if (r.supplement_revenue == null) cur.suppGap += 1;
    else cur.supp += num(r.supplement_revenue);
    if (r.is_partial) cur.partial = true;
    booked.set(y, cur);
  }

  const writtenOff = new Map<string, number>();
  const nonCollect = new Map<string, number>();
  for (const d of deals) {
    const y = String(d.build_completed_date ?? "").slice(0, 4);
    if (!/^\d{4}$/.test(y)) continue;
    const wo = num(d.balance_written_off);
    if (wo > 0) writtenOff.set(y, (writtenOff.get(y) ?? 0) + wo);
    const nc = num(d.non_commissionable);
    if (nc > 0) nonCollect.set(y, (nonCollect.get(y) ?? 0) + nc);
  }
  const built = new Map<string, { rev: number; build: number; supp: number; n: number; unbilled: number }>();
  for (const d of deals) {
    const y = String(d.build_completed_date ?? "").slice(0, 4);
    if (!/^\d{4}$/.test(y)) continue;
    const cur = built.get(y) ?? { rev: 0, build: 0, supp: 0, n: 0, unbilled: 0 };
    const sp = splitRevenue(d);
    cur.rev += sp.revenue; cur.build += sp.build; cur.supp += sp.supplement; cur.n += 1;
    if (sp.revenue <= 0) cur.unbilled += 1;
    built.set(y, cur);
  }

  const years = [...new Set([...booked.keys(), ...built.keys(), ...legacy.keys()])].sort();
  return years.map((year) => {
    const b = booked.get(year) ?? null;
    const c = built.get(year) ?? { rev: 0, build: 0, supp: 0, n: 0, unbilled: 0 };
    const variance = b ? c.rev - b.rev : null;
    // More than a tenth of the year's builds carrying no invoice means the CRM
    // simply wasn't the system of record yet — don't invite a comparison.
    const thin = c.n === 0 || c.unbilled / Math.max(1, c.n) > 0.1;
    const lg = legacy.get(year) ?? null;
    // Fall back to the Zoho series only where the CRM genuinely cannot answer.
    // Never blend the two inside one year — that would invent a number neither
    // system reported.
    const useLegacy = thin && !!lg && lg.rev > 0;
    return {
      year,
      officialRevenue: off.get(year)?.rev ?? null,
      officialSupplements: off.get(year)?.supp ?? null,
      officialBuildRevenue: off.has(year)
        ? (off.get(year) as { rev: number; supp: number }).rev - (off.get(year) as { supp: number }).supp
        : null,
      officialRevenueSources: [...(off.get(year)?.srcRev ?? [])].sort(),
      officialSupplementSources: [...(off.get(year)?.srcSupp ?? [])].sort(),
      booked: b ? b.rev : null,
      bookedSupplement: b && (b.supp > 0 || b.suppGap === 0) ? b.supp : null,
      bookedSupplementGapMonths: b ? b.suppGap : 0,
      supplementItemizedMonths: b ? b.itemMonths : 0,
      supplementNotItemizedMonths: b ? b.nonItemMonths : 0,
      bookedItemizedPortion: b ? b.itemRev : 0,
      badDebt: b && b.feeGap === 0 ? b.badDebt : b ? b.badDebt : null,
      lateFee: b && b.feeGap === 0 ? b.lateFee : b ? b.lateFee : null,
      crmWrittenOff: writtenOff.get(year) ?? 0,
      nonCollect: nonCollect.get(year) ?? 0,
      built: c.rev,
      builtBuild: c.build,
      builtSupplement: c.supp,
      supplementPct: c.rev > 0 ? (c.supp / c.rev) * 100 : null,
      builds: c.n,
      unbilledBuilds: c.unbilled,
      variance,
      variancePct: b && b.rev > 0 ? ((c.rev - b.rev) / b.rev) * 100 : null,
      crmCoverageThin: thin,
      legacyProduction: lg ? lg.rev : null,
      legacyDeals: lg ? lg.n : null,
      productionIsLegacy: useLegacy,
      production: useLegacy ? (lg as { rev: number }).rev : thin ? null : c.rev,
      productionBuilds: useLegacy ? (lg as { n: number }).n : thin ? null : c.n,
      inProgress: year === thisYear,
      bookedPartial: !!b?.partial,
    };
  });
}
